import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {formatDateFns} from '@shared/helpers/date'
import {isEmpty} from '@shared/helpers/lang'

import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {
    overwriteHopeDate,
    updateHopeDate,
    OrderSheetProductsState,
    getInitialState,
} from '@web/slices/orderSheetProducts/slice'

import {ProductsReducers, MakeSliceOptions} from './interfaces'

export default (options?: MakeSliceOptions) =>
    createSlice<
        OrderSheetProductsState,
        ProductsReducers,
        'orderSheetProducts',
        SliceSelectors<OrderSheetProductsState>
    >({
        name: 'orderSheetProducts',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(updateHopeDate, (state, {payload}) => {
                    const {orderSheetProductIds, selectedDate} = payload
                    if (isEmpty(orderSheetProductIds)) {
                        return state
                    }

                    const hopeDeliveryDate = formatDateFns(selectedDate ?? '', 'yyyy-MM-dd')
                    const hopeDeliveryTime = formatDateFns(selectedDate ?? '', 'HH:mm')

                    return state.map((product) => {
                        const isMatched = orderSheetProductIds.some((id) => id === product.id)
                        if (isMatched) {
                            return {
                                ...product,
                                arrivalGuaranteeDateTime: selectedDate,
                                deliveryInfo: {
                                    ...product.deliveryInfo,
                                    hopeDelivery: product.deliveryInfo.hopeDelivery
                                        ? {
                                              ...product.deliveryInfo.hopeDelivery,
                                              hopeDeliveryDate,
                                              hopeDeliveryTime,
                                          }
                                        : null,
                                },
                            }
                        }
                        return product
                    })
                })
                .addCase(overwriteHopeDate.fulfilled, (state, {payload}) => {
                    const {orderSheetProductIds} = payload
                    if (isEmpty(orderSheetProductIds)) {
                        return
                    }

                    return state.map((product) => {
                        const isMatched = orderSheetProductIds.some((id) => id === product.id)
                        if (isMatched) {
                            const hopeDeliveryDate = formatDateFns(product.arrivalGuaranteeDateTime ?? '', 'yyyy-MM-dd')
                            const hopeDeliveryTime = formatDateFns(product.arrivalGuaranteeDateTime ?? '', 'HH:mm')
                            return {
                                ...product,
                                deliveryInfo: {
                                    ...product.deliveryInfo,
                                    hopeDelivery: product.deliveryInfo.hopeDelivery
                                        ? {
                                              ...product.deliveryInfo.hopeDelivery,
                                              hopeDeliveryDate,
                                              hopeDeliveryTime,
                                          }
                                        : null,
                                },
                            }
                        }
                        return product
                    })
                })
            options?.extraReducers?.(builder)
            builder.addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                return payload.orderSheetProducts ?? state
            })
        },
    })
